"use client";

import React from "react";
import { DesignSystemFixtureMetadata } from "@features/design-system/design-system-types";
import { FixtureInvariantMeta } from "@features/design-system/ui/fixture/fixture-invariant-meta";
import { FixtureLibsUsed } from "@features/design-system/ui/fixture/fixture-libs-used";
import { FixtureMetadata } from "@features/design-system/ui/fixture/fixture-metadata";
import { FixturePropertyImpactTable } from "@features/design-system/ui/fixture/fixture-property-impact-table";
import {
  FixturePropsTable,
  PropertyDefinition,
} from "@features/design-system/ui/fixture/fixture-props-table";
import { FixtureSection } from "@features/design-system/ui/fixture/fixture-section";
import { FixtureWrapper } from "@features/design-system/ui/fixture/fixture-wrapper";

// Самый простой целевой компонент-шаблон для демонстрации
const DemoComponent: React.FC<{ text?: string; disabled?: boolean }> = ({
  text = "Пример",
  disabled = false,
}) => {
  return (
    <button
      type="button"
      className={`inline-flex items-center rounded-md border px-3 py-1.5 text-sm ${
        disabled
          ? "cursor-not-allowed border-zinc-300 bg-zinc-100 text-zinc-400"
          : "border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50"
      }`}
      disabled={disabled}
    >
      {text}
    </button>
  );
};

const TemplateFixture = () => {
  // 1) Документация свойств публичного API
  const generalProps: PropertyDefinition[] = [
    {
      name: "text",
      type: "string",
      defaultValue: `"Пример"`,
      description: "Текстовая метка внутри компонента.",
    },
    {
      name: "disabled",
      type: "boolean",
      defaultValue: "false",
      description: "Флаг неактивного состояния. Отключает взаимодействие и меняет стиль.",
    },
  ];

  return (
    <>
      {/* 1. Описание */}
      <FixtureSection title="1. Описание" id="about">
        <div className="space-y-4">
          <p>
            Этот файл служит <strong>шаблоном фикстуры</strong> для документирования и визуализации
            компонентов в дизайн‑системе. Он демонстрирует полную структуру из 11 обязательных
            секций согласно ТЗ.
          </p>
          <p>
            <strong>Когда использовать этот шаблон:</strong> При создании новой фикстуры скопируйте
            этот файл, замените <code>DemoComponent</code> на целевой компонент, обновите все секции
            актуальными данными и моками.
          </p>
          <p>
            <strong>Ключевые возможности шаблона:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-1">
            <li>Полная структура из 11 секций согласно ТЗ.</li>
            <li>Примеры использования всех UI-компонентов фикстур.</li>
            <li>Комментарии-подсказки для каждой секции.</li>
            <li>Готовые примеры инвариантов с использованием FixtureInvariantMeta.</li>
            <li>Placeholder-тексты для быстрой замены.</li>
          </ul>
        </div>
      </FixtureSection>

      {/* 2. Meta */}
      <FixtureSection title="2. Meta" id="meta">
        <FixtureMetadata
          componentName="DemoComponent"
          sourcePath="src/features/design-system/fixtures/template.fixture.tsx"
          domain="Design System (шаблоны и примеры)"
          dependencies={[
            {
              path: "react",
              description: "библиотека для создания UI-компонентов",
            },
            {
              path: "tailwind-css",
              description: "utility-first CSS framework для стилизации",
            },
          ]}
          version="1.0.0 (шаблонная версия)"
        />
      </FixtureSection>

      {/* 3. Бизнес-контекст */}
      <FixtureSection title="3. Бизнес‑контекст" id="business-context">
        <div className="space-y-4">
          <p>
            <strong>Роли и сценарии использования:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Разработчик фронтенда:</strong> использует шаблон для создания новых фикстур
              компонентов. Ожидаемая ценность: готовая структура, экономия времени, единообразие
              документации.
            </li>
            <li>
              <strong>Дизайнер:</strong> изучает структуру фикстуры для понимания, как компоненты
              документируются. Ожидаемая ценность: ясное представление о состояниях компонента и
              edge-cases.
            </li>
            <li>
              <strong>Аналитик/Заказчик:</strong> просматривает примеры компонентов для оценки
              поведения в разных сценариях. Ожидаемая ценность: визуальная демонстрация всех
              состояний.
            </li>
          </ul>
          <p>
            <strong>Ключевые ограничения:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-1">
            <li>
              Шаблон использует упрощённый <code>DemoComponent</code> только для демонстрации
              структуры.
            </li>
            <li>
              При создании реальной фикстуры необходимо заменить все placeholder-тексты актуальными
              данными.
            </li>
            <li>Все 11 секций обязательны для полноценной документации компонента.</li>
            <li>
              Инварианты должны покрывать минимум 5 случаев: базовый, пустые данные, загрузка,
              ошибка и 2+ доменно-специфичных.
            </li>
          </ul>
        </div>
      </FixtureSection>

      {/* 4. Пропсы и типы */}
      <FixtureSection title="4. Пропсы и типы" id="props-types">
        <FixturePropsTable properties={generalProps} />
        <div className="mt-4 space-y-3">
          <FixturePropertyImpactTable
            properties={[
              {
                propertyPath: "text",
                type: "string",
                renderImpact: "Определяет текстовое содержимое кнопки",
                missingBehavior: 'Используется значение по умолчанию "Пример"',
              },
              {
                propertyPath: "disabled",
                type: "boolean",
                renderImpact: "Переключает интерактивность и визуальный стиль",
                missingBehavior: "По умолчанию false, кнопка активна",
              },
            ]}
          />
        </div>
      </FixtureSection>

      {/* 5. Инварианты */}
      <FixtureSection title="5. Инварианты" id="invariants">
        {/* 5.1 Базовый */}
        <FixtureSection title="5.1 Базовый" id="invariant-base" variant="inner">
          <FixtureInvariantMeta
            id="base-render"
            name="Базовое состояние компонента"
            goal="Проверка стандартного рендеринга с параметрами по умолчанию."
            preconditions="Компонент получает минимальный набор пропсов или значения по умолчанию."
            inputData={['text = "Пример"', "disabled = false"]}
            expectedResult='Кнопка отображается с текстом "Пример", активна для взаимодействия, имеет стандартные стили.'
            edgeCases="Нет."
          />
          <FixturePropsTable
            properties={[
              {
                name: "text",
                type: "string",
                value: '"Пример"',
                description: "Текст по умолчанию",
              },
              {
                name: "disabled",
                type: "boolean",
                value: "false",
                description: "Активное состояние",
              },
            ]}
          />
          <FixtureWrapper label="Базовый" variant="default">
            <div className="flex gap-4">
              <DemoComponent />
            </div>
          </FixtureWrapper>
        </FixtureSection>

        {/* 5.2 Кастомный текст */}
        <FixtureSection title="5.2 Кастомный текст" id="invariant-custom-text" variant="inner">
          <FixtureInvariantMeta
            id="custom-text"
            name="Пользовательский текст"
            goal="Проверка отображения произвольной текстовой метки."
            preconditions="Передан проп text с пользовательским значением."
            inputData={['text = "Создать"', "disabled = false"]}
            expectedResult='Кнопка отображает текст "Создать", остаётся интерактивной.'
            edgeCases="Очень длинный текст (200+ символов) должен корректно переноситься или обрезаться."
          />
          <FixturePropsTable
            properties={[
              {
                name: "text",
                type: "string",
                value: '"Создать"',
                description: "Пользовательский текст",
              },
              {
                name: "disabled",
                type: "boolean",
                value: "false",
                description: "Активное состояние",
              },
            ]}
          />
          <FixtureWrapper label="Кастомный текст" variant="default">
            <div className="flex gap-4">
              <DemoComponent text="Создать" />
            </div>
          </FixtureWrapper>
        </FixtureSection>

        {/* 5.3 Неактивное состояние */}
        <FixtureSection title="5.3 Неактивное состояние" id="invariant-disabled" variant="inner">
          <FixtureInvariantMeta
            id="disabled-state"
            name="Отключённое состояние"
            goal="Проверка поведения компонента при disabled=true."
            preconditions="Передан проп disabled = true."
            inputData={['text = "Недоступно"', "disabled = true"]}
            expectedResult="Кнопка неактивна, стили приглушены, курсор not-allowed, отсутствует hover-эффект."
            edgeCases="Нет."
          />
          <FixturePropsTable
            properties={[
              {
                name: "text",
                type: "string",
                value: '"Недоступно"',
                description: "Текстовая метка",
              },
              {
                name: "disabled",
                type: "boolean",
                value: "true",
                description: "Неактивное состояние",
              },
            ]}
          />
          <FixtureWrapper label="Disabled" variant="default">
            <div className="flex gap-4">
              <DemoComponent text="Недоступно" disabled />
            </div>
          </FixtureWrapper>
        </FixtureSection>

        {/* 5.4 Пустой текст (Edge case) */}
        <FixtureSection title="5.4 Пустой текст" id="invariant-empty-text" variant="inner">
          <FixtureInvariantMeta
            id="empty-text"
            name="Пустая текстовая метка"
            goal="Проверка поведения при передаче пустой строки."
            preconditions='Передан проп text = "".'
            inputData={['text = ""', "disabled = false"]}
            expectedResult="Кнопка отображается без текста, занимает минимальное пространство (только padding)."
            edgeCases="Это крайний случай. В реальных сценариях рекомендуется валидировать наличие текста."
          />
          <FixturePropsTable
            properties={[
              {
                name: "text",
                type: "string",
                value: '""',
                description: "Пустая строка",
              },
              {
                name: "disabled",
                type: "boolean",
                value: "false",
                description: "Активное состояние",
              },
            ]}
          />
          <FixtureWrapper label="Пустой текст" variant="default">
            <div className="flex gap-4">
              <DemoComponent text="" />
            </div>
          </FixtureWrapper>
        </FixtureSection>

        {/* 5.5 Галерея состояний */}
        <FixtureSection title="5.5 Галерея состояний" id="invariant-gallery" variant="inner">
          <FixtureInvariantMeta
            id="state-gallery"
            name="Галерея всех состояний"
            goal="Визуальное сравнение всех основных состояний компонента на одном экране."
            preconditions="Нет."
            inputData={["Набор компонентов с разными комбинациями пропсов"]}
            expectedResult="Отображаются все варианты: базовый, кастомный текст, disabled, пустой текст."
            edgeCases="Нет."
          />
          <FixtureWrapper label="Галерея" variant="default">
            <div className="flex flex-wrap gap-4">
              <DemoComponent text="По умолчанию" />
              <DemoComponent text="Действие" />
              <DemoComponent text="Недоступно" disabled />
              <DemoComponent text="" />
            </div>
          </FixtureWrapper>
        </FixtureSection>
      </FixtureSection>

      {/* 6. Примеры и edge-cases */}
      <FixtureSection title="6. Примеры и крайние случаи" id="edge-cases">
        <div className="space-y-4">
          <p>
            <strong>Полнота данных:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Полные данные:</strong> Все пропсы заполнены → стандартный рендер.
            </li>
            <li>
              <strong>Минимальные данные:</strong> Только обязательные пропсы → используются
              значения по умолчанию.
            </li>
            <li>
              <strong>Пустые данные:</strong> text = &quot;&quot; → кнопка без текста.
            </li>
            <li>
              <strong>Очень длинный текст:</strong> 200+ символов → необходима обрезка или перенос.
            </li>
          </ul>
          <p>
            <strong>Специальные символы:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-1">
            <li>Эмодзи в тексте должны корректно отображаться.</li>
            <li>HTML-сущности должны быть экранированы.</li>
          </ul>
        </div>
      </FixtureSection>

      {/* 7. Accessibility */}
      <FixtureSection title="7. Доступность" id="accessibility">
        <ul className="ml-6 list-disc space-y-1">
          <li>Кнопка доступна с клавиатуры: фокус по Tab, активация по Enter/Space.</li>
          <li>
            Атрибут <code>disabled</code> применяется семантически для скринридеров.
          </li>
          <li>Контрастность текста соответствует WCAG AA (минимум 4.5:1 для обычного текста).</li>
          <li>
            Курсор <code>not-allowed</code> для disabled состояния сигнализирует о недоступности.
          </li>
          <li>
            Hover-эффекты видны только в активном состоянии, что помогает пользователям понять
            интерактивность.
          </li>
        </ul>
      </FixtureSection>

      {/* 8. Используемые библиотеки */}
      <FixtureSection title="8. Используемые библиотеки" id="design-system-used">
        <FixtureLibsUsed
          items={[
            { label: "React", href: "https://react.dev/" },
            { label: "Next.js (App Router)", href: "https://nextjs.org/docs/app" },
            { label: "Tailwind CSS", href: "https://tailwindcss.com/docs" },
          ]}
        />
      </FixtureSection>

      {/* 9. Рекомендации */}
      <FixtureSection title="9. Рекомендации" id="guidelines">
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Всегда передавайте осмысленный текст в проп <code>text</code>, избегайте пустых строк в
            продакшене.
          </li>
          <li>
            Используйте <code>disabled</code> для блокировки действий во время выполнения запросов
            или при отсутствии прав.
          </li>
          <li>
            При создании новой фикстуры: скопируйте этот шаблон, замените компонент и обновите все
            секции.
          </li>
          <li>
            Убедитесь, что все инварианты используют компонент <code>FixtureInvariantMeta</code> для
            единообразия.
          </li>
          <li>
            Добавляйте минимум 5 инвариантов: базовый, пустые данные, загрузка/ошибка и 2+
            доменно-специфичных.
          </li>
        </ul>
      </FixtureSection>

      {/* 10. Бизнес-правила */}
      <FixtureSection title="10. Бизнес‑правила" id="business-rules">
        <div className="space-y-3">
          <ul className="ml-6 list-disc space-y-1">
            <li>
              Инвариант 1 (видимость текста): если <code>text</code> пустая строка или отсутствует,
              компонент использует значение по умолчанию &quot;Пример&quot;.
            </li>
            <li>
              Инвариант 2 (интерактивность): при <code>disabled = true</code> компонент не реагирует
              на клики, hover-эффекты отключены, курсор <code>not-allowed</code>.
            </li>
            <li>
              Инвариант 3 (стилизация): активное состояние имеет hover-эффект (bg-zinc-50),
              неактивное — приглушённые цвета (bg-zinc-100, text-zinc-400).
            </li>
            <li>
              Инвариант 4 (контентная длина): длинные значения <code>text</code> не должны ломать
              layout; рекомендуется ограничение max-width или text-overflow.
            </li>
            <li>
              Инвариант 5 (семантика): компонент использует нативный <code>&lt;button&gt;</code> для
              корректной работы с клавиатурой и скринридерами.
            </li>
          </ul>
        </div>
      </FixtureSection>

      {/* 11. Примечания/допущения */}
      <FixtureSection title="11. Примечания/допущения" id="notes-assumptions">
        <div className="space-y-3">
          <p>
            <strong>Контекстуальные допущения:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              Компонент <code>DemoComponent</code> создан исключительно для демонстрации структуры
              шаблона. В реальной фикстуре используйте целевой компонент из кодовой базы.
            </li>
            <li>
              Все placeholder-тексты (например, &quot;Пример&quot;, &quot;Создать&quot;) должны быть
              заменены на актуальные значения при адаптации шаблона.
            </li>
            <li>
              Структура из 11 секций является обязательной согласно ТЗ. Пропуск секций приведёт к
              неполноте документации.
            </li>
            <li>
              Для реальных компонентов необходимо добавить доменно-специфичные инварианты,
              отражающие бизнес-правила и особенности поведения.
            </li>
          </ul>
          <p>
            <strong>TODO/риски:</strong>
          </p>
          <ul className="ml-6 list-disc space-y-1">
            <li>
              При адаптации шаблона: обновить список зависимостей в секции Meta на основе реальных
              импортов компонента.
            </li>
            <li>
              Проверить accessibility с помощью инструментов (axe, lighthouse) для подтверждения
              соответствия WCAG AA.
            </li>
            <li>
              Убедиться, что все инварианты покрывают реальные use-cases, а не только теоретические
              сценарии.
            </li>
          </ul>
        </div>
      </FixtureSection>
    </>
  );
};

export default TemplateFixture;

export const meta: DesignSystemFixtureMetadata = {
  group: "Templates",
  parentItem: "Base",
  title: "Template Fixture",
  description:
    "Полный шаблон фикстуры дизайн‑системы с 11 секциями и примерами всех UI-компонентов",
};
